import AdminTenant from 'models/adminTenant';
import { DirectoryRef } from 'models/identityProvider';
import Norm from 'models/norm';
import Tenant from 'models/tenant';

export type TenantWidgetTenant = Tenant | AdminTenant;

//The ISO standards a partner widget may be configured for: the union of what the selected CUSTOMERS run, not
//what the partner itself has installed. globalDataCache.norms only ever holds the signed-in tenant's standards,
//so on a partner dashboard it describes the wrong organization entirely - a consultancy that runs no standard
//of its own would get an empty list and a dead widget.
//Org unit widgets keep using globalDataCache, where the cache and the data do describe the same tenant.
export function getISONormsForTenants(tenants: TenantWidgetTenant[], tenantIds: string[]): Norm[] {
  const selected = tenantIds.length > 0 ? tenants.filter((t) => tenantIds.includes(getTenantWidgetTenantId(t))) : tenants;

  const byIsoNormId = new Map<number, Norm>();
  selected.forEach((tenant) => {
    if (!(tenant instanceof AdminTenant)) return;

    tenant.norms.forEach((norm) => {
      if (norm.isoNormId && !byIsoNormId.has(norm.isoNormId)) {
        byIsoNormId.set(norm.isoNormId, norm);
      }
    });
  });

  return [...byIsoNormId.values()];
}

export function getTenantWidgetTenantId(tenant: TenantWidgetTenant | undefined): string {
  if (!tenant) return '';

  return tenant instanceof Tenant ? tenant.id : tenant.tenantId;
}

//The directory the tenant signs in with; undefined for a tenant without one (a container)
export function getTenantWidgetTenantDirectory(tenant: TenantWidgetTenant | undefined): DirectoryRef | undefined {
  if (!tenant?.providerTenantId) return undefined;

  return tenant;
}

export function getTenantWidgetTenantName(tenant: TenantWidgetTenant | undefined): string {
  if (!tenant) return '';

  return tenant instanceof Tenant ? tenant.name : tenant.displayName || tenant.name;
}

//Chart data points and series carry the tenant they describe, so a click can resolve it without going through the
//label on the axis. Two customers of the same partner may share a display name, in which case a lookup by name
//silently returns whichever matched first - and since a drill-down builds a tid/ouid deep link, that opens a
//tenant the user did not click. The charts pass our own objects straight back to the click handler, so an extra
//field on them survives untouched. There is deliberately no by-name counterpart to this: use the id.
export type WithTenantId<T> = T & { tenantId: string };

//Stamp the tenant on every point of a series at once, so the individual point literals stay about the data.
export function withTenantId<T>(points: T[], tenantId: string): WithTenantId<T>[] {
  return points.map((point) => ({ ...point, tenantId }));
}

export function getTenantWidgetTenantById(
  tenants: TenantWidgetTenant[],
  tenantId: string | undefined,
): TenantWidgetTenant | undefined {
  if (!tenantId) return undefined;

  return tenants.find((tenant) => getTenantWidgetTenantId(tenant) === tenantId);
}

export function sortTenantWidgetTenantsByName(tenants: TenantWidgetTenant[]): TenantWidgetTenant[] {
  return tenants.sort((a, b) => getTenantWidgetTenantName(a).localeCompare(getTenantWidgetTenantName(b)));
}

export function getConfiguredTenantIds(configTenants: string[] | undefined, tenants: TenantWidgetTenant[]): string[] {
  if (configTenants && configTenants.length > 0) {
    return configTenants;
  }

  return tenants.map(getTenantWidgetTenantId).filter((tenantId) => tenantId !== '');
}
